import classNames from 'classnames/bind'
import { groupBy } from 'lodash'

import { LocaleSwitchHeader } from '../components/localeSwitchHeader/LocaleSwitchHeader'
import { CustomAccordion } from '../components/customAccordion/CustomAccordion'
import { useCustomAccordion } from '../components/customAccordion/useCustomAccordion'
import { OuterCard } from '../components/outerCard/OuterCard'
import { useAnswerTranslation } from '../hooks/useAnswerTranslation'
import { formatDate } from '../../utils/utils'
import { createMoneyAnswers } from '../candidateTasks/candidateReview/money/moneyAnswers'

import type {
  CandidateFormSubmission,
  CandidateFormSubmissionAnswer
} from '@percent/partner-dashboard/services/grants/grantsService.types'
import { Accordion, Flex, MarkdownPreview, Typography } from '@percent/lemonade'
import styles from './SubmittedInfo.module.scss'

const cx = classNames.bind(styles)

const stripHeadings = (input: string) =>
  input
    .split('\n')
    .map(line => line.replace(/^\s*#{1,6}\s*/, ''))
    .join('\n')

const formatAnswerContent = (type: CandidateFormSubmissionAnswer['type'], content: string) => {
  switch (type) {
    case 'date':
    case 'scheduled_event':
    case 'payment_date': {
      const date = new Date(content)

      return Number.isNaN(date.getTime()) ? content : formatDate(date, 'yyyy-MM-dd')
    }
    default:
      return stripHeadings(content)
  }
}

export const groupAnswersByPage = (formAnswers: CandidateFormSubmission['formAnswers']) => {
  const grouped = groupBy(formAnswers, 'pageId')

  return Object.entries(grouped).map(([pageId, answers]) => ({
    pageId,
    pageTitle: answers[0]?.pageTitle || '',
    answers
  }))
}

export function PageGroup({
  page,
  displayName
}: Readonly<{
  page: ReturnType<typeof groupAnswersByPage>[number]
  displayName: string
}>) {
  const {
    selectedLocale,
    setSelectedLocale,
    originalLocale,
    isTranslated,
    translationAvailable,
    getAnswerContent,
    getQuestionLabel
  } = useAnswerTranslation(page.answers)

  const moneyAnswers = createMoneyAnswers(
    page.answers
      .filter(answer => answer.type === 'money')
      .map(answer => ({ key: answer.questionKey, answer: answer.answer }))
  )

  const visibleAnswers = page.answers.filter(
    answer => !(answer.type === 'money' && moneyAnswers.isPairedCurrency(answer.questionKey))
  )

  const renderAnswer = (answer: CandidateFormSubmissionAnswer) =>
    answer.type === 'money' && answer.answer
      ? moneyAnswers.format(answer.questionKey, answer.answer)
      : formatAnswerContent(answer.type, getAnswerContent(answer))

  const itemsIds = visibleAnswers.map(answer => answer.id)
  const { toggleAll, handleAccordionChange, getAccordionValue, label } = useCustomAccordion(itemsIds)

  return (
    <Flex direction="column" gap={12} data-testid={`submitted-info-page-${page.pageId}`}>
      {page.pageTitle !== displayName && <Typography variant="h5">{page.pageTitle}</Typography>}
      <OuterCard
        customBgColour={isTranslated && translationAvailable ? 'info' : 'default'}
        expandAll={{ label, onClick: toggleAll }}
        customHeader={
          isTranslated ? (
            <LocaleSwitchHeader
              selectedLocale={selectedLocale}
              setSelectedLocale={setSelectedLocale}
              originalLocale={originalLocale}
              translationAvailable={translationAvailable}
            />
          ) : undefined
        }
      >
        <div className={cx(styles.innerCard, styles.defaultBorder)} data-testid="inner-card">
          {visibleAnswers.map(answer => (
            <CustomAccordion
              key={answer.id}
              multiple
              value={getAccordionValue(answer.id)}
              onChange={handleAccordionChange(answer.id)}
              className={cx(styles.innerCardSectionAccordion, styles.defaultTopBorder)}
            >
              <Accordion.Item value={answer.id} data-testid={`submitted-info-answer-${answer.id}`}>
                <Accordion.Control data-testid={`submitted-info-answer-${answer.id}-label`}>
                  {getQuestionLabel(answer)}
                </Accordion.Control>
                <Accordion.Panel data-testid={`submitted-info-answer-${answer.id}-content`}>
                  <MarkdownPreview content={renderAnswer(answer)} fontSize="bodySmall" />
                </Accordion.Panel>
              </Accordion.Item>
            </CustomAccordion>
          ))}
        </div>
      </OuterCard>
    </Flex>
  )
}
